import React, { useEffect, useRef, useState, useMemo } from 'react';
import Select from 'react-select';

const CollapsibleMultiSelect = ({
  options,
  value,
  onChange,
  isLoading,
  placeholder,
  id,
  maxVisibleRows = 2,
  classNamePrefix = 'rs',
  styles: userStyles = {},
  ...rest
}: any) => {
  const [expanded, setExpanded] = useState(false);
  const [overflowing, setOverflowing] = useState(false);
  const [collapsedHeight, setCollapsedHeight] = useState(maxVisibleRows * 38 + 8); // px
  const bodyRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = bodyRef.current;
    if (!el) return;

    // Get the actual height of one row
    const firstChip = el.querySelector('.rs__multi-value') as HTMLElement;
    const chipHeight = firstChip?.offsetHeight || 32;
    const visibleHeight = chipHeight * maxVisibleRows + 8; // padding

    setCollapsedHeight(visibleHeight);

    // Calculate how many full rows are present
    const totalHeight = el.scrollHeight;
    const fullRows = Math.floor(totalHeight / chipHeight);

    // Show toggle only if more than maxVisibleRows rows exist
    setOverflowing(fullRows > maxVisibleRows);
  }, [value, options, maxVisibleRows]);

  const styles = useMemo(
    () => ({
      menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
      menu: (base: any) => ({ ...base, zIndex: 9999 }),
      control: (base: any) => ({ ...base, minHeight: 38 }),
      valueContainer: (base: any) => ({ ...base, paddingTop: 6, paddingBottom: 6 }),
      ...userStyles
    }),
    [userStyles]
  );

  return (
    <div className="w-full">
      <div
        ref={bodyRef}
        className="relative rounded-md border border-gray-300"
        style={{
          maxHeight: expanded ? 'none' : `${collapsedHeight}px`,
          overflow: expanded ? 'visible' : 'hidden',
          transition: 'max-height 0.25s ease'
        }}>
        {!expanded && overflowing && (
          <div
            className="pointer-events-none absolute bottom-0 left-0 right-0 h-8"
            style={{
              background: 'linear-gradient(to bottom, rgba(255,255,255,0), rgba(255,255,255,1))'
            }}
          />
        )}

        <Select
          id={id}
          className="react-select-container"
          classNamePrefix={classNamePrefix}
          options={options}
          value={value}
          onChange={onChange}
          isMulti
          isLoading={isLoading}
          placeholder={placeholder}
          closeMenuOnSelect={false}
          menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
          menuPosition="fixed"
          styles={{
            ...styles,
            control: (base: any) => ({
              ...base,
              minHeight: 38,
              border: 'none',
              boxShadow: 'none'
            })
          }}
          {...rest}
        />
      </div>

      {overflowing && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className={`mt-2 inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-medium
        border-indigo-500 text-indigo-600 bg-white hover:bg-indigo-50 hover:shadow-sm transition`}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="14"
            height="14"
            viewBox="0 0 20 20"
            fill="currentColor"
            className={expanded ? 'rotate-180 transition' : 'transition'}>
            <path
              fillRule="evenodd"
              d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z"
            />
          </svg>
          {expanded ? 'Show Less' : `Show All (${Array.isArray(value) ? value.length : 0})`}
        </button>
      )}
    </div>
  );
};

export default CollapsibleMultiSelect;
